import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import FileUpload from '../../../components/ui/FileUpload';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../utils/supabaseClient';

const ComplaintForm = ({ onSubmit, isSubmitting }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    contactNumber: '',
    email: '',
    lineId: '',
    category: '',
    description: '',
    attachments: [],
    privacyConsent: false
  });

  const [errors, setErrors] = useState({});
  const [characterCount, setCharacterCount] = useState(0);

  const complaintCategories = [
    { 
      value: 'public-services', 
      label: 'บริการสาธารณะ',
      description: 'ปัญหาเกี่ยวกับการให้บริการของหน่วยงาน'
    },
    { 
      value: 'infrastructure', 
      label: 'โครงสร้างพื้นฐาน',
      description: 'ถนน ไฟฟ้า ประปา และสาธารณูปโภค'
    },
    { 
      value: 'environment', 
      label: 'สิ่งแวดล้อม',
      description: 'มลพิษ ขยะ และปัญหาสิ่งแวดล้อม'
    },
    { 
      value: 'safety', 
      label: 'ความปลอดภัย',
      description: 'ปัญหาความปลอดภัยในชุมชน'
    },
    { 
      value: 'administration', 
      label: 'การบริหารงาน',
      description: 'ปัญหาการบริหารงานของหน่วยงาน'
    },
    { 
      value: 'other', 
      label: 'อื่นๆ',
      description: 'ปัญหาอื่นๆ ที่ไม่อยู่ในหมวดหมู่ข้างต้น'
    }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'กรุณากรอกชื่อ-นามสกุล';
    } else if (formData?.fullName?.trim()?.length < 2) {
      newErrors.fullName = 'ชื่อ-นามสกุลต้องมีอย่างน้อย 2 ตัวอักษร';
    }

    if (!formData?.contactNumber?.trim()) {
      newErrors.contactNumber = 'กรุณากรอกหมายเลขโทรศัพท์';
    } else if (!/^[0-9]{9,10}$/?.test(formData?.contactNumber?.replace(/[-\s]/g, ''))) {
      newErrors.contactNumber = 'หมายเลขโทรศัพท์ไม่ถูกต้อง (9-10 หลัก)';
    }

    if (formData?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'รูปแบบอีเมลไม่ถูกต้อง';
    }

    if (!formData?.lineId?.trim()) {
      newErrors.lineId = 'กรุณากรอกไอดีไลน์';
    } else if (!/^[a-zA-Z0-9._-]+$/.test(formData?.lineId)) {
      newErrors.lineId = 'ไอดีไลน์ไม่ถูกต้อง (ใช้ได้เฉพาะตัวอักษร ตัวเลข และ _ . -)';
    }

    if (!formData?.category) {
      newErrors.category = 'กรุณาเลือกหมวดหมู่เรื่องร้องเรียน';
    }

    if (!formData?.description?.trim()) {
      newErrors.description = 'กรุณากรอกรายละเอียดเรื่องร้องเรียน';
    } else if (formData?.description?.trim()?.length < 20) {
      newErrors.description = 'รายละเอียดต้องมีอย่างน้อย 20 ตัวอักษร';
    } else if (formData?.description?.trim()?.length > 2000) {
      newErrors.description = 'รายละเอียดต้องไม่เกิน 2,000 ตัวอักษร';
    }

    // Validate file attachments
    if (formData?.attachments?.length > 5) {
      newErrors.attachments = 'สามารถแนบไฟล์ได้สูงสุด 5 ไฟล์';
    }

    // Validate privacy consent
    if (!formData?.privacyConsent) {
      newErrors.privacyConsent = 'กรุณายืนยันการยินยอมเผยแพร่ข้อมูลส่วนบุคคล';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (field === 'description') {
      setCharacterCount(value?.length);
    }

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleFileUpload = (files) => {
    setFormData(prev => ({
      ...prev,
      attachments: files
    }));

    // Clear file upload errors
    if (errors?.attachments) {
      setErrors(prev => ({
        ...prev,
        attachments: ''
      }));
    }
  };

  const generateTicketNumber = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    // ใช้ timestamp (milliseconds) + random เพื่อลดโอกาสซ้ำ
    const timestamp = Date.now().toString().slice(-6); // 6 หลักสุดท้ายของ timestamp
    const random = Math.floor(Math.random() * 100).toString().padStart(2, '0');
    return `TH-${year}${month}${day}-${timestamp}${random}`;
  };

  const uploadFiles = async (files) => {
    const uploadedFiles = [];
    
    for (const file of files) {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `complaints/${fileName}`;
      
      const { data, error } = await supabase.storage
        .from('complaint-attachments')
        .upload(filePath, file);
      
      if (error) {
        console.error('Error uploading file:', error);
        continue;
      }
      
      const { data: { publicUrl } } = supabase.storage
        .from('complaint-attachments')
        .getPublicUrl(filePath);
      
      uploadedFiles.push({
        name: file.name,
        url: publicUrl,
        path: filePath
      });
    }
    
    return uploadedFiles;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    try {
      // Upload files if any
      let uploadedFiles = [];
      if (formData.attachments && formData.attachments.length > 0) {
        uploadedFiles = await uploadFiles(formData.attachments);
      }

      // Insert complaint with retry logic for duplicate ticket numbers
      let complaint = null;
      let complaintError = null;
      let ticketNo = null;
      let maxRetries = 5;
      let retryCount = 0;

      while (retryCount < maxRetries && !complaint) {
        // Generate new ticket number for each retry
        ticketNo = generateTicketNumber();
        
        const { data, error } = await supabase
          .from('complaints')
          .insert({
            ticket_no: ticketNo,
            full_name: formData.fullName.trim(),
            contact_number: formData.contactNumber.trim(),
            email: formData.email?.trim() || null,
            line_id: formData.lineId.trim(),
            category: formData.category,
            description: formData.description.trim(),
            attachments: uploadedFiles,
            privacy_consent: formData.privacyConsent,
            status: 'received',
            created_at: new Date().toISOString()
          })
          .select()
          .single();

        if (error) {
          // ตรวจสอบว่าเป็น error จาก duplicate ticket number หรือไม่
          if (error.code === '23505' || error.message?.includes('duplicate') || error.message?.includes('unique')) {
            // Ticket number ซ้ำ ลองใหม่
            retryCount++;
            if (retryCount >= maxRetries) {
              complaintError = error;
              break;
            }
            // รอสักครู่ก่อน retry เพื่อให้ timestamp เปลี่ยน
            await new Promise(resolve => setTimeout(resolve, 100));
            continue;
          } else {
            // Error อื่นๆ
            complaintError = error;
            break;
          }
        }

        complaint = data;
      }

      if (complaintError || !complaint) {
        console.error('Error creating complaint:', complaintError);
        throw new Error('เกิดข้อผิดพลาดในการบันทึกข้อมูล กรุณาลองใหม่อีกครั้ง');
      }

      // Insert initial event
      const { error: eventError } = await supabase
        .from('complaint_events')
        .insert({
          complaint_id: complaint.id,
          status: 'received',
          description: 'ระบบรับเรื่องเรียบร้อย',
          created_at: new Date().toISOString()
        });

      if (eventError) {
        console.error('Error creating event:', eventError);
      }

      // Call LINE Notify function (if implemented)
      try {
        await supabase.functions.invoke('line-notify', {
          body: {
            ticket_no: ticketNo,
            full_name: formData.fullName.trim(),
            category: formData.category,
            description: formData.description.trim().substring(0, 100) + '...'
          }
        });
      } catch (lineError) {
        console.error('LINE Notify error:', lineError);
        // Don't fail the submission if LINE fails
      }

      // Call parent onSubmit with success data
      onSubmit({
        success: true,
        ticketNo: ticketNo,
        complaintId: complaint.id
      });

    } catch (error) {
      console.error('Submission error:', error);
      onSubmit({
        success: false,
        error: error.message || 'เกิดข้อผิดพลาดในการส่งเรื่องร้องเรียน'
      });
    }
  };

  const getCharacterCountColor = () => {
    if (characterCount > 2000) return 'text-error';
    if (characterCount > 1800) return 'text-warning';
    return 'text-muted-foreground';
  };

  const resetForm = () => {
    // Clean up file URLs to prevent memory leaks
    formData?.attachments?.forEach(file => {
      if (file?.url) {
        URL.revokeObjectURL(file?.url);
      }
    });

    setFormData({
      fullName: '',
      contactNumber: '',
      email: '',
      lineId: '',
      category: '',
      description: '',
      attachments: [],
      privacyConsent: false
    });
    setErrors({});
    setCharacterCount(0);
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-government p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-primary rounded-md flex items-center justify-center">
          <Icon name="FileText" size={20} color="white" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-foreground">
            แบบฟอร์มยื่นเรื่องร้องเรียน
          </h2>
          <p className="text-sm text-muted-foreground">
            กรุณากรอกข้อมูลให้ครบถ้วนและถูกต้อง
          </p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Personal Information Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
            ข้อมูลผู้ยื่นเรื่อง
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="ชื่อ-นามสกุล"
              type="text"
              placeholder="กรอกชื่อ-นามสกุลของท่าน"
              value={formData?.fullName}
              onChange={(e) => handleInputChange('fullName', e?.target?.value)}
              error={errors?.fullName}
              required
              className="col-span-1"
            />

            <Input
              label="หมายเลขโทรศัพท์"
              type="tel"
              placeholder="08X-XXX-XXXX"
              value={formData?.contactNumber}
              onChange={(e) => handleInputChange('contactNumber', e?.target?.value)}
              error={errors?.contactNumber}
              required
              className="col-span-1"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="อีเมล (ไม่บังคับ)"
              type="email"
              placeholder="example@email.com"
              description="สำหรับรับการแจ้งเตือนผลการดำเนินการ"
              value={formData?.email}
              onChange={(e) => handleInputChange('email', e?.target?.value)}
              error={errors?.email}
              className="col-span-1"
            />
            <Input
              label="ไอดีไลน์"
              type="text"
              placeholder="your.line.id"
              value={formData?.lineId}
              onChange={(e) => handleInputChange('lineId', e?.target?.value)}
              error={errors?.lineId}
              required
              className="col-span-1"
            />
          </div>
        </div>

        {/* Complaint Details Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
            รายละเอียดเรื่องร้องเรียน
          </h3>

          <Select
            label="หมวดหมู่เรื่องร้องเรียน"
            placeholder="เลือกหมวดหมู่ที่เหมาะสม"
            options={complaintCategories}
            value={formData?.category}
            onChange={(val) => handleInputChange('category', val)}
            error={errors?.category}
            required
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              รายละเอียดเรื่องร้องเรียน
              <span className="text-error ml-1">*</span>
            </label>
            <textarea
              className={`w-full rounded-md border bg-background px-3 py-2 text-sm outline-none transition-colors resize-y min-h-[140px] ${errors?.description ? 'border-error focus:border-error' : 'border-border focus:border-primary/60'}`}
              placeholder="กรุณากรอกรายละเอียดเรื่องร้องเรียนให้ชัดเจน เช่น สถานที่ วันเวลา เหตุการณ์ บุคคลที่เกี่ยวข้อง ฯลฯ"
              value={formData?.description}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
              required
            />
            <div className={`text-xs mt-1 ${getCharacterCountColor()}`}>
              ตัวอักษร {characterCount}/2000
            </div>
            {errors?.description && (
              <p className="text-xs text-error mt-1">{errors?.description}</p>
            )}
          </div>
        </div>

        {/* File Upload Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
            เอกสารหลักฐานประกอบ (ไม่บังคับ)
          </h3>
          
          <FileUpload
            label="แนบไฟล์หลักฐาน"
            description="แนบรูปภาพ เอกสาร หรือหลักฐานที่เกี่ยวข้องกับเรื่องร้องเรียน เพื่อช่วยในการพิจารณา"
            accept="image/*,.pdf,.doc,.docx,.txt"
            maxFiles={5}
            maxFileSize={10485760} // 10MB
            value={formData?.attachments}
            onChange={handleFileUpload}
            error={errors?.attachments}
          />

          {/* File Upload Guidelines */}
          <div className="bg-accent/20 border border-accent/30 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={16} className="text-primary mt-0.5 flex-shrink-0" />
              <div className="text-sm space-y-2">
                <p className="font-medium text-foreground">คำแนะนำการแนบไฟล์:</p>
                <ul className="text-muted-foreground space-y-1 ml-3">
                  <li>• รูปภาพควรมีความชัดเจนและเห็นรายละเอียดได้ดี</li>
                  <li>• ไฟล์เอกสารควรอ่านได้ชัดเจน</li>
                  <li>• สามารถแนบได้สูงสุด 5 ไฟล์ ไฟล์ละไม่เกิน 10MB</li>
                  <li>• รองรับไฟล์: JPG, PNG, PDF, Word, TXT</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Privacy Consent Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
            การยินยอมเผยแพร่ข้อมูลส่วนบุคคล
          </h3>
          
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="relative">
                  <input
                    type="checkbox"
                    id="privacyConsent"
                    checked={formData.privacyConsent}
                    onChange={(e) => handleInputChange('privacyConsent', e.target.checked)}
                    className="sr-only"
                  />
                  <div 
                    className={`mt-1 h-4 w-4 rounded border-2 transition-all duration-200 cursor-pointer flex items-center justify-center ${
                      errors.privacyConsent 
                        ? 'border-red-500 bg-red-50' 
                        : formData.privacyConsent
                        ? 'border-green-500 bg-green-500'
                        : 'border-gray-300 bg-white hover:border-gray-400'
                    }`}
                    onClick={() => handleInputChange('privacyConsent', !formData.privacyConsent)}
                  >
                    {formData.privacyConsent && (
                      <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </div>
                <div className="flex-1">
                  <label htmlFor="privacyConsent" className="text-sm font-medium text-foreground cursor-pointer">
                    ข้าพเจ้ายินยอมให้เผยแพร่ข้อมูลส่วนบุคคลของข้าพเจ้าเพื่อการดำเนินการตามเรื่องร้องเรียน
                    <span className="text-error ml-1">*</span>
                  </label>
                  {errors.privacyConsent && (
                    <p className="text-xs text-error mt-1">{errors.privacyConsent}</p>
                  )}
                </div>
              </div>
              
              <div className="bg-background/50 border border-border rounded-md p-3 text-sm text-muted-foreground">
                <div className="space-y-2">
                  <p className="font-medium text-foreground">รายละเอียดการยินยอม:</p>
                  <ul className="space-y-1 ml-4">
                    <li>• ข้อมูลส่วนบุคคลของท่านจะถูกใช้เพื่อการดำเนินการตามเรื่องร้องเรียนเท่านั้น</li>
                    <li>• ข้อมูลจะถูกเก็บรักษาอย่างปลอดภัยตามพระราชบัญญัติคุ้มครองข้อมูลส่วนบุคคล พ.ศ. 2562</li>
                    <li>• เจ้าหน้าที่จะติดต่อกลับเฉพาะในกรณีที่จำเป็นเพื่อการดำเนินการเท่านั้น</li>
                    <li>• ข้อมูลจะไม่ถูกเผยแพร่ต่อบุคคลที่สามโดยไม่ได้รับความยินยอมจากท่าน</li>
                    <li>• ท่านสามารถขอถอนความยินยอมได้ตลอดเวลาโดยติดต่อเจ้าหน้าที่</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
          <Button
            type="submit"
            variant="default"
            loading={isSubmitting}
            iconName="Send"
            iconPosition="left"
            className="sm:order-2"
            fullWidth
          >
            {isSubmitting ? 'กำลังส่งเรื่องร้องเรียน...' : 'ส่งเรื่องร้องเรียน'}
          </Button>
          
          <Button
            type="button"
            variant="outline"
            onClick={resetForm}
            iconName="RotateCcw"
            iconPosition="left"
            className="sm:order-1"
            fullWidth
          >
            ล้างข้อมูล
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ComplaintForm;